import React from 'react';

interface UseAutoSubmitCodeLikeInputParams {
	requiredLength: number;
	clearAfterErrorMs: number;
	notifyUser: (message: string) => void;
	submit: (value: string) => Promise<void>;
	getErrorMessage?: (error: unknown) => string | undefined;
	errorMessageFallback: string;
}

interface UseAutoSubmitCodeLikeInputResult {
	value: string;
	errorNonce: number;
	inputRef: React.RefObject<HTMLInputElement>;
	onChange: (value: string) => void;
}

export function useAutoSubmitCodeLikeInput({
	requiredLength,
	clearAfterErrorMs,
	notifyUser,
	submit,
	getErrorMessage,
	errorMessageFallback,
}: UseAutoSubmitCodeLikeInputParams): UseAutoSubmitCodeLikeInputResult {
	const [value, setValue] = React.useState('');
	const [errorNonce, setErrorNonce] = React.useState(0);
	const inputRef = React.useRef<HTMLInputElement>(null as any);
	const clearValueTimeoutRef = React.useRef<number | null>(null);
	const submitLockRef = React.useRef(false);

	React.useEffect(() => {
		return () => {
			if (clearValueTimeoutRef.current) window.clearTimeout(clearValueTimeoutRef.current);
		};
	}, []);

	const scheduleClear = React.useCallback(() => {
		if (clearValueTimeoutRef.current) window.clearTimeout(clearValueTimeoutRef.current);
		clearValueTimeoutRef.current = window.setTimeout(() => {
			setValue('');
			clearValueTimeoutRef.current = null;
		}, clearAfterErrorMs);
	}, [clearAfterErrorMs]);

	const onChange = React.useCallback(
		(nextValue: string) => {
			if (clearValueTimeoutRef.current) {
				window.clearTimeout(clearValueTimeoutRef.current);
				clearValueTimeoutRef.current = null;
			}

			setValue(nextValue);
			if (nextValue.length < requiredLength || submitLockRef.current) return;

			submitLockRef.current = true;
			submit(nextValue)
				.catch((error) => {
					setErrorNonce((n) => n + 1);
					inputRef.current?.focus();
					notifyUser(getErrorMessage?.(error) || errorMessageFallback);
					scheduleClear();
				})
				.finally(() => {
					submitLockRef.current = false;
				});
		},
		[errorMessageFallback, getErrorMessage, notifyUser, requiredLength, scheduleClear, submit],
	);

	return {value, errorNonce, inputRef, onChange};
}

